import React, { useState, useEffect, useRef } from "react";
import { Button, Spinner, Modal, Dropdown, ProgressBar, Form } from "react-bootstrap";
import { generatesummary, contractReminders } from "../../../../../services/CommonService";
import { toast } from "react-toastify";
import ReactMarkdown from "react-markdown";
import remarkGFM from "remark-gfm";
import {
  FaCopy, FaDownload, FaRegStopCircle, FaFilePdf, FaFileWord, FaBook,
  FaChartLine, FaHistory, FaClipboardList, FaBell, FaSearchPlus, FaSearchMinus, FaArrowLeft
} from "react-icons/fa";
import { LuSpeech } from "react-icons/lu";
import html2pdf from "html2pdf.js";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import mammoth from "mammoth";
import { useLocation, useNavigate } from "react-router-dom";
import { ChartColumn } from "../../../../../assets/svgicons/icons";
import { MdOutlineDateRange } from "react-icons/md";

const SleeveFilesSummaryGenerator = ({ selectedDoc: initialDoc, onClose, loading, setLoading }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentDoc, setCurrentDoc] = useState(null);
  const synth = window.speechSynthesis;
  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  const [summaries, setSummaries] = useState({
    summary: "",
    timeline: [],
    term: "",
  });
  const [documentContent, setDocumentContent] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("summary");
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [speechSpeed, setSpeechSpeed] = useState(1);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [selectedReminderData, setSelectedReminderData] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [panelWidth, setPanelWidth] = useState(50);
  const [isResizing, setIsResizing] = useState(false);
  const [noDocumentData, setNoDocumentData] = useState(false);
  const [isLoadingDoc, setIsLoadingDoc] = useState(true);
  const [email, setEmail] = useState("");
  const [reminderNote, setReminderNote] = useState('');
  const [reminderSettings, setReminderSettings] = useState({
    one_day_before: false,
    one_week_before: false,
    one_month_before: false,
    custom_frequency: false,
    custom_frequency_type: "daily",
    custom_frequency_days: "",
  });
  const containerRef = useRef(null);

  // Resizing functionality
  const startResizing = React.useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const stopResizing = React.useCallback(() => {
    setIsResizing(false);
  }, []);

  const resize = React.useCallback(
    (e) => {
      if (isResizing && containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const newWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;
        setPanelWidth(Math.min(Math.max(newWidth, 20), 80));
      }
    },
    [isResizing]
  );

  useEffect(() => {
    if (isResizing) {
      window.addEventListener("mousemove", resize);
      window.addEventListener("mouseup", stopResizing);
    }
    return () => {
      window.removeEventListener("mousemove", resize);
      window.removeEventListener("mouseup", stopResizing);
    };
  }, [isResizing, resize, stopResizing]);

  // Initialize document data
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const docParam = queryParams.get('doc');
    let doc;

    if (docParam) {
      try {
        doc = JSON.parse(decodeURIComponent(docParam));
      } catch (e) {
        const storedDoc = localStorage.getItem('currentDocument');
        if (storedDoc) doc = JSON.parse(storedDoc);
      }
    } else if (initialDoc) {
      doc = initialDoc;
    } else {
      const storedDoc = localStorage.getItem('currentDocument');
      if (storedDoc) doc = JSON.parse(storedDoc);
    }

    if (doc) {
      setCurrentDoc(doc);
      setNoDocumentData(false);
      setIsLoadingDoc(false);
      fetchSummaryFromDatabase(doc.envelope_id || doc.envelopeId || doc.file_name);
    } else {
      setNoDocumentData(true);
      setIsLoadingDoc(false);
    }
  }, [location.search, initialDoc]);

  // Fetch summary from database
  const fetchSummaryFromDatabase = async (docIdentifier) => {
    try {
      const response = await generatesummary({ envelope_id: docIdentifier });
      if (response.data?.summaries) {
        let timelineData = [];
        const rawTimeline = response.data.summaries.contract_timelines;
        if (rawTimeline) {
          try {
            const jsonString = rawTimeline.replace(/```json/g, '').replace(/```/g, '').trim();
            timelineData = JSON.parse(jsonString);
          } catch (error) {
            timelineData = [];
          }
        }
        setSummaries({
          summary: response.data.summaries.contract_summary || "",
          term: response.data.summaries.contract_terms_conditions || "",
          timeline: Array.isArray(timelineData) ? timelineData : []
        });
      }
    } catch (error) {
      console.error("Error fetching summaries:", error);
    }
  };

  // Load document content
  useEffect(() => {
    const loadDocumentContent = async () => {
      if (!currentDoc || !currentDoc.sign_file_path) return;
      setLoadingProgress(0);
      const fileNameLower = currentDoc.file_name.toLowerCase();

      try {
        if (fileNameLower.endsWith(".pdf")) {
          setDocumentContent(null);
          setLoadingProgress(100);
        } else if (fileNameLower.endsWith(".docx")) {
          const response = await fetch(currentDoc.sign_file_path);
          const arrayBuffer = await response.arrayBuffer();
          const result = await mammoth.convertToHtml({ arrayBuffer });
          setDocumentContent(result.value);
          setLoadingProgress(100);
        } else if ([".jpg", ".jpeg", ".png", ".webp"].some((ext) => fileNameLower.endsWith(ext))) {
          setDocumentContent(currentDoc.sign_file_path);
          setLoadingProgress(100);
        } else {
          const response = await fetch(currentDoc.sign_file_path);
          const text = await response.text();
          setDocumentContent(text);
          setLoadingProgress(100);
        }
      } catch (error) {
        setDocumentContent("Failed to load document content.");
        toast.error("Failed to load document content.");
        setLoadingProgress(100);
      }
    };
    loadDocumentContent();
  }, [currentDoc]);

  // Generate summary
  const handleGenerateSummary = async () => {
    if (!currentDoc) return;
    setSummaryLoading(true);
    setSummaries({ summary: "", timeline: [], term: "" });

    try {
      const summaryResponse = await generatesummary({
        envelope_id: Number(currentDoc.envelopeId),
        file_name: currentDoc.fileName || currentDoc.file_name,
      });

      let timelineData = [];
      const rawTimeline = summaryResponse?.data?.summaries?.contract_timelines;
      if (rawTimeline) {
        try {
          const jsonString = rawTimeline.replace(/```json/g, '').replace(/```/g, '').trim();
          timelineData = JSON.parse(jsonString);
        } catch (error) {
          timelineData = [];
        }
      }

      setSummaries({
        summary: summaryResponse?.data?.summaries?.contract_summary || "Summary not available.",
        term: summaryResponse?.data?.summaries?.contract_terms_conditions || "Term not available.",
        timeline: Array.isArray(timelineData) ? timelineData : [],
      });
      toast.success("Content generated successfully!");
    } catch (error) {
      toast.error("Failed to generate content. Please try again later.");
    } finally {
      setSummaryLoading(false);
    }
  };

  // Copy summary
  const handleCopySummary = (summaryType) => {
    if (summaries[summaryType]) {
      const plainText = Array.isArray(summaries[summaryType])
        ? summaries[summaryType].map(item => `${item.date} ${item.event}`).join("\n")
        : summaries[summaryType].replace(/[*_#`]+/g, "");
      navigator.clipboard.writeText(plainText)
        .then(() => toast.success(`${summaryType.charAt(0).toUpperCase() + summaryType.slice(1)} copied!`))
        .catch(() => toast.error("Failed to copy content"));
    }
  };

  const handleStopSpeech = () => {
    synth.cancel();
    setIsSpeaking(false);
  };

  const handleSpeechToggle = (contentType) => {
    if (isSpeaking) {
      handleStopSpeech();
    } else if (summaries[contentType]) {
      const plainText = Array.isArray(summaries[contentType])
        ? summaries[contentType].map(item => `${item.date} ${item.event}`).join("\n")
        : summaries[contentType].replace(/[*_#>]/g, "");
      const utterance = new SpeechSynthesisUtterance(plainText);
      utterance.rate = speechSpeed;
      if (selectedVoice) utterance.voice = selectedVoice;

      utterance.onend = () => setIsSpeaking(false);

      setIsSpeaking(true);
      synth.speak(utterance);
    }
  };

  // Download functionality
  const handleDownloadSummary = async (summaryType, format) => {
    if (!summaries[summaryType]) {
      toast.error("No content available to download");
      return;
    }

    try {
      const summaryContent = Array.isArray(summaries[summaryType])
        ? summaries[summaryType].map(item => `${item.date} ${item.event}`).join("\n")
        : summaries[summaryType].replace(/[*_#`]+/g, "");
      const filename = `${currentDoc.file_name.split(".")[0]}_${summaryType}.${format}`;

      if (format === "pdf") {
        const element = document.createElement("div");
        element.innerHTML = `
          <div >
            <h1 style="font-weight: bold; font-size: 18pt; margin-bottom: 20px; border-bottom: 1px solid #eee; padding-bottom: 10px;">
              ${summaryType.charAt(0).toUpperCase() + summaryType.slice(1)}
            </h1>
            <div style="font-size: 12pt; line-height: 1.6;">
              ${summaryContent.replace(/\n/g, "<br>")}
            </div>
          </div>
        `;
        await html2pdf().from(element).set({
          margin: 10,
          filename: filename,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, letterRendering: true, useCORS: true },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        }).save();
      }
      toast.success(`Content downloaded as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error("Failed to download content");
    }
  };

  // Reminder functionality
  const handleSetReminder = (timelineItem) => {
    setSelectedReminderData(timelineItem);
    setShowReminderModal(true);
  };

  const handleSaveReminder = async () => {
    if (!selectedReminderData) return;
    const emailArray = email.split(',').map(email => email.trim()).filter(email => email);
    if (emailArray.length === 0) {
      toast.error("Please enter at least one email address");
      return;
    }

    try {
      await contractReminders({
        envelope_id: Number(currentDoc.envelopeId),
        file_name: currentDoc.file_name,
        event_name: selectedReminderData.event || "No event specified",
        event_date: selectedReminderData.date || new Date().toISOString().split("T")[0],
        email: emailArray,
        reminder_settings: reminderSettings,
        description: reminderNote,
      });
      toast.success(`Reminder set for ${selectedReminderData.date || 'the specified date'}`);
      setShowReminderModal(false);
    } catch (error) {
      toast.error("Failed to set reminder. Please try again.");
    }
  };


const handleBack = () => {
    navigate("/manage/files");
  };


  // Loading state
  if (isLoadingDoc) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ height: "100vh", backgroundColor: "#f8f9fa" }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <h5 className="text-muted">Loading Document...</h5>
          <p className="text-muted">Please wait while we fetch the document information.</p>
        </div>
      </div>
    );
  }

  // Error state
  if (!currentDoc || noDocumentData) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ height: "100vh", backgroundColor: "#f8f9fa" }}>
        <div className="text-center">
          <div className="alert alert-danger border-0 shadow-sm" style={{ maxWidth: "400px" }}>
            <h5 className="mb-3">Document Data Not Found</h5>
            <Button variant="primary" onClick={() => window.history.back()}>
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="document-viewer-container" ref={containerRef} style={{
      height: "100vh",
      display: "flex",
      width: "100%",
      position: "relative",
      overflow: "hidden",
      backgroundColor: "#f8f9fa"
    }}>
      {/* Document Preview Section */}
      <div className="document-preview-section" style={{
        width: `${panelWidth}%`,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        backgroundColor: "white",
        boxShadow: "2px 0 10px rgba(0,0,0,0.1)"
      }}>
        {/* Header */}
        <div className="document-preview-header p-3" style={{
          borderBottom: "1px solid #e9ecef",
          backgroundColor: "white"
        }}>
          <div className="d-flex justify-content-between align-items-center w-100">
            <h6 className="mb-0 fw-bold text-dark">
              {currentDoc?.fileName || currentDoc?.file_name}
            </h6>

            <div className="zoom-controls">
              <button
                className="zoom-btn"
                onClick={() => setZoomLevel(prev => Math.max(0.5, prev - 0.1))}
              >
                <FaSearchMinus />
              </button>
              <button
                className="zoom-btn"
                onClick={() => setZoomLevel(prev => Math.min(2, prev + 0.1))}
              >
                <FaSearchPlus />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="document-preview-content flex-grow-1" style={{ overflow: "auto" }}>
          {loadingProgress < 100 ? (
            <div className="d-flex flex-column justify-content-center align-items-center h-100">
              <ProgressBar now={loadingProgress} label={`${loadingProgress}%`} style={{ width: "80%" }} />
              <p className="mt-3 text-muted">Loading document content...</p>
            </div>
          ) : currentDoc?.file_name?.toLowerCase().endsWith(".pdf") ? (
            <div style={{ height: "100%", transform: `scale(${zoomLevel})`, transformOrigin: "top center" }}>
              <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                <Viewer
                  fileUrl={currentDoc.sign_file_path}
                  plugins={[defaultLayoutPluginInstance]}
                  style={{ height: "100%" }}
                />
              </Worker>
            </div>
          ) : currentDoc?.file_name?.toLowerCase().endsWith(".docx") ? (
            <div
              className="docx-content p-4"
              style={{
                height: "100%",
                overflow: "auto",
                backgroundColor: "white",
                transform: `scale(${zoomLevel})`,
                transformOrigin: "top center"
              }}
              dangerouslySetInnerHTML={{ __html: documentContent }}
            />
          ) : [".jpg", ".jpeg", ".png", ".webp"].some((ext) =>
            currentDoc?.file_name?.toLowerCase().endsWith(ext)
          ) ? (
            <div className="d-flex justify-content-center align-items-center h-100">
              <img
                src={documentContent}
                alt={currentDoc.file_name}
                style={{
                  maxHeight: "90%",
                  maxWidth: "90%",
                  objectFit: "contain",
                  transform: `scale(${zoomLevel})`
                }}
              />
            </div>
          ) : (
            <div className="p-4" style={{ height: "100%", overflow: "auto" }}>
              <ReactMarkdown remarkPlugins={[remarkGFM]}>
                {documentContent}
              </ReactMarkdown>
            </div>
          )}
        </div>
      </div>

      {/* Resize Handle */}
      <div
        style={{
          width: "8px",
          height: "100%",
          backgroundColor: "#e9ecef",
          cursor: "col-resize",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          zIndex: 10
        }}
        onMouseDown={startResizing}
      >
        <div style={{
          width: "2px",
          height: "40px",
          backgroundColor: "#adb5bd",
          borderRadius: "1px"
        }} />
      </div>

      {/* Summary Section */}
      <div className="summary-section">
        {/* Header */}
        <div className="summary-header">
          <h6 className="mb-0 fw-bold text-dark d-flex align-items-center justify-content-between">
            <span className="me-2"><ChartColumn /></span>
            Document Analysis
          </h6>

          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={handleBack}
            style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
          >
            <FaArrowLeft /> Back
          </button>
        </div>
        {/* Tabs */}
        <div className="tab-buttons">
          {[
            { key: "summary", label: "Summary", icon: <FaClipboardList /> },
            { key: "timeline", label: "Timeline", icon: <MdOutlineDateRange /> },
            { key: "term", label: "Terms", icon: <FaBook /> }
          ].map(({ key, label, icon }) => (
            <button
              key={key}
              className={`tab-btn ${activeTab === key ? 'active' : ''}`}
              onClick={() => setActiveTab(key)}
            >
              <span className="me-2">{icon}</span>
              {label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="tab-content flex-grow-1" style={{ overflow: "auto", backgroundColor: "#f8f9fa" }}>
          {summaryLoading ? (
            <div className="d-flex flex-column justify-content-center align-items-center h-100">
              <div className="spinner-border text-primary mb-3" />
              <p className="text-muted">Generating content...</p>
            </div>
          ) : (
            <div className="summary-content">
              {summaries[activeTab] ? (
                <>
                  {activeTab === "timeline" ? (
                    <div className="custom-timeline">
                      {summaries.timeline.length > 0 ? (
                        summaries.timeline.map((item, index) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          let itemDate = null;
                          try {
                            if (item.date) {
                              const parts = item.date.split("/");
                              if (parts.length === 3) {
                                itemDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
                                itemDate.setHours(0, 0, 0, 0);
                              }
                            }
                          } catch (e) {
                            console.error("Invalid date format:", item.date);
                          }
                          const isFutureDate =
                            itemDate && !isNaN(itemDate.getTime()) && itemDate >= today;

                          return (
                            <div key={index} className="custom-timeline-item">
                              <div className="timeline-marker"></div>
                              <div className="timeline-content">
                                <div className="timeline-date">{item.date || "No date specified"}</div>
                                <div className="timeline-event">{item.event || "No event details"}</div>
                              </div>
                              {isFutureDate && (
                                <button
                                  className="reminder-btn-pro"
                                  onClick={() => handleSetReminder(item)}
                                >
                                  <FaBell className="icon-remind" />
                                  <span>Remind Me</span>
                                </button>
                              )}

                            </div>
                          );
                        })
                      ) : (
                        <div className="no-timeline">
                          <FaHistory className="icon" />
                          <p>No timeline data available</p>
                        </div>
                      )}
                    </div>

                  ) : (
                    <div className="bg-white p-4 rounded shadow-sm">
                      <ReactMarkdown remarkPlugins={[remarkGFM]}
                        components={{
                          h1: ({ node, ...props }) => <h1 className="fw-bold fs-4 mb-3" {...props} />,
                          h2: ({ node, ...props }) => <h2 className="fw-semibold fs-5 mt-3 mb-2" {...props} />,
                          h3: ({ node, ...props }) => <h3 className="fw-semibold fs-6 mt-2 mb-2" {...props} />,
                          p: ({ node, ...props }) => <p className="mb-2 text-dark" {...props} />,
                          ul: ({ node, ...props }) => <ul className="ms-3 mb-2" {...props} />,
                          li: ({ node, ...props }) => <li className="mb-1" {...props} />,
                          strong: ({ node, ...props }) => <strong className="text-dark" {...props} />,
                        }}>
                        {summaries[activeTab]}
                      </ReactMarkdown>
                    </div>
                  )}

                  {/* Action Buttons */}
                  {activeTab !== "timeline" && summaries[activeTab] && (<div
                    className="floating-buttons d-flex gap-2"
                  >
                    <Dropdown>
                      <Dropdown.Toggle variant="primary" size="sm" className="rounded shadow-lg">
                        <FaDownload className="me-1" />
                        Export
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => handleDownloadSummary(activeTab, "pdf")}>
                          <FaFilePdf className="me-2 text-danger" />
                          PDF
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => handleDownloadSummary(activeTab, "docx")}>
                          <FaFileWord className="me-2 text-primary" />
                          Word
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>

                    <Button
                      variant="success"
                      size="sm"
                      className="rounded shadow-lg"
                      onClick={() => handleCopySummary(activeTab)}
                    >
                      <FaCopy className="me-1" />
                      Copy
                    </Button>

                    <Button
                      variant={isSpeaking ? "danger" : "info"}
                      size="sm"
                      className="rounded shadow-lg"
                      onClick={() => isSpeaking ? handleStopSpeech() : handleSpeechToggle(activeTab)}
                    >
                      {isSpeaking ? <FaRegStopCircle className="me-1" /> : <LuSpeech className="me-1" />}
                      {isSpeaking ? "Stop" : "Read"}
                    </Button>
                  </div>
                  )}
                </>
              ) : (
                <div className="text-center text-muted py-5">
                  <FaClipboardList className="mb-3" style={{ fontSize: "3rem", opacity: 0.3 }} />
                  <p>No {activeTab} generated yet</p>
                  <p className="small">Click the generate button below to create content</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Generate Button */}
        <div className="p-3" style={{ borderTop: "1px solid #e9ecef" }}>
          <button
            className="w-100 btn-save"
            onClick={handleGenerateSummary}
            disabled={summaryLoading}
            size="lg"
          >
            {summaryLoading ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Generating...
              </>
            ) : (
              <>
                <FaChartLine className="me-2" />
                Generate Analysis
              </>
            )}
          </button>
        </div>
      </div>

      {/* Reminder Modal */}
      <Modal
        show={showReminderModal}
        onHide={() => setShowReminderModal(false)}
        centered
        backdrop="static"
        size="lg"
        className="fade"
      >
        <div className="modal-content">
          {/* Header */}
          <div className="modal-header">
            <h5 className="modal-title">Set Reminder</h5>
            <button
              type="button"
              className="bg-transparent"
              aria-label="Close"
              onClick={() => setShowReminderModal(false)}
            >
              <i className="bi bi-x-circle"></i>
            </button>
          </div>


          {/* Body */}
          <div className="modal-body px-4 py-4">
            <form>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold text-muted mb-1">Date</label>
                  <div className="py-2 px-3 bg-light rounded border">
                    {selectedReminderData?.date ? (
                      <p className="mb-0 fw-bold">{selectedReminderData.date}</p>
                    ) : (
                      <p className="mb-0 text-muted">No date specified</p>
                    )}
                  </div>
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold text-muted mb-1">Event</label>
                  <div className="py-2 px-3 bg-light rounded border">
                    {selectedReminderData?.event ? (
                      <p className="mb-0 fw-bold">{selectedReminderData.event}</p>
                    ) : (
                      <p className="mb-0 text-muted">No event details</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Reminder Settings */}
              <div className="mb-3">
                <label className="form-label fw-semibold text-muted mb-1">Reminder Settings</label>
                <div className="row">
                  <div className="col-md-4">
                    <div className="form-check mb-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="dayBefore"
                        checked={reminderSettings.one_day_before}
                        onChange={(e) =>
                          setReminderSettings({
                            ...reminderSettings,
                            one_day_before: e.target.checked,
                          })
                        }
                      />
                      <label className="form-check-label" htmlFor="dayBefore">
                        1 Day Before
                      </label>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-check mb-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="weekBefore"
                        checked={reminderSettings.one_week_before}
                        onChange={(e) =>
                          setReminderSettings({
                            ...reminderSettings,
                            one_week_before: e.target.checked,
                          })
                        }
                      />
                      <label className="form-check-label" htmlFor="weekBefore">
                        1 Week Before
                      </label>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-check mb-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="monthBefore"
                        checked={reminderSettings.one_month_before}
                        onChange={(e) =>
                          setReminderSettings({
                            ...reminderSettings,
                            one_month_before: e.target.checked,
                          })
                        }
                      />
                      <label className="form-check-label" htmlFor="monthBefore">
                        1 Month Before
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="mb-3">
                <label className="form-label fw-semibold text-muted ,b-1">Email Addresses*</label>
                <input
                  type="text"
                  className="form-control rounded"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter email addresses separated by commas"
                  required
                />
                <small className="text-muted">Separate multiple emails with commas</small>
              </div>

              {/* Notes */}
              <div className="mb-3">
                <label className="form-label fw-semibold text-muted mb-1">Additional Notes</label>
                <textarea
                  rows={3}
                  className="form-control rounded"
                  value={reminderNote}
                  onChange={(e) => setReminderNote(e.target.value)}
                  placeholder="Add any important notes about this reminder..."
                />
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button
              type="button"
              className="btn-cancel"
              onClick={() => setShowReminderModal(false)}
            >
              Cancel
            </button>
            <button type="button" className="btn-save" onClick={handleSaveReminder}>
              Set Reminder
            </button>
          </div>

        </div>
      </Modal>

    </div>
  );
};

export default SleeveFilesSummaryGenerator;


